import { useTranslation } from "react-i18next";
import { HeartIcon } from "lucide-react";
import { Badge } from "@ds/ui/badge";
import { cn } from "@ds/lib/utils";
import type { SessionLogSummaryModel } from "../logs.model";
import { useLogText } from "../use-log-text";

/**
 * ONE SESSION IN HISTORY — SHAPED LIKE A DECK CARD, WITH THE DATE WHERE THE DURATION GOES.
 *
 * 🚨 IT WAS THREE CRAMPED LINES AND IT IS A CARD NOW. The row printed "Wed, 25 Aug · Deadlift
 * + Swing AMRAP" as one 13px line, then a score, then the ratings — so the TITLE, which is the
 * only thing anybody scans a history list for, was half of a sentence at caption size. The
 * owner's instruction was to show what a deck card shows, with the date in the place the card
 * gives the estimate. So the structure is the card's, top to bottom:
 *
 *   deck card   title + 14 min   →   5 rounds · cap 12   →   badged movements   →   RX · Scaled
 *   history     Wed, 25 Aug      →   title              →   RESULT line + format   →   RX · felt
 *
 * ⚠️ THE CARD IT WAS COPIED FROM HAS BEEN REDRAWN SINCE (BUD-339) — it is the metcon alone
 * now, its estimate sits beside the name rather than above it, and there is no strength line.
 * This row was not redrawn with it and the two are no longer the same shape. That is a
 * deliberate non-change and not an oversight: a history row answers "what did I do", and the
 * one thing it must not copy is a card's arrangement of a session it does not hold.
 *
 * ⚠️ AND WHAT IT CANNOT SHOW, SAID OUT LOUD: the movements. A card lists them because it is
 * holding a drawn session; a log stores the title, the format, the score and the ratings and
 * NOT the blocks (`session_logs` has no line table). Printing a movement list here would mean
 * redrawing every row from its seed — one request per row, for a list. What the movements are
 * is one tap away instead, on the session this row opens.
 *
 * THE WHOLE ROW OPENS THE SESSION, and the heart is a sibling rather than a child: nesting a
 * button inside a button is invalid markup and leaves a screen reader unable to reach the
 * inner one. Same arrangement the session screen's blocks use.
 */
export function SessionLogRow({
  log,
  onOpen,
  onToggleBenchmark,
}: {
  log: SessionLogSummaryModel;
  onOpen: (log: SessionLogSummaryModel) => void;
  onToggleBenchmark: (log: SessionLogSummaryModel) => void;
}) {
  const { t } = useTranslation("logs");
  const text = useLogText();
  const debrief = text.debrief(log);

  return (
    <li className="relative rounded-card bg-card">
      {/* The heart sits OVER the card's own tap target rather than inside it — see the header.
          Positioned so the title below can use the full width without wrapping under it. */}
      <button
        type="button"
        onClick={() => onToggleBenchmark(log)}
        aria-label={text.heart(log)}
        aria-pressed={log.isFavourite}
        data-testid={`heart-${log.id}`}
        className="absolute top-1.5 right-1.5 z-10 flex size-touch items-center justify-center"
      >
        <HeartIcon
          className={cn("size-4", log.isFavourite ? "text-accent" : "text-line-strong")}
          fill={log.isFavourite ? "currentColor" : "none"}
          aria-hidden
        />
      </button>

      <button
        type="button"
        onClick={() => onOpen(log)}
        aria-label={t("history.open", { title: log.title })}
        data-testid={`log-${log.id}`}
        className="flex w-full flex-col px-gutter py-3.5 text-left"
      >
        {/* WHERE THE CARD PUTS ITS ESTIMATE, the history puts the day it was trained. */}
        <span className="text-legal pr-touch font-bold text-muted-foreground">
          {text.day(log)}
        </span>

        <span className="text-section mt-1.5 pr-touch">{log.title}</span>

        <span className="text-support mt-2.5 text-foreground-muted">{text.result(log)}</span>

        <span className="mt-2.5 flex flex-wrap items-center gap-2">
          <Badge variant="neutral">{text.load(log)}</Badge>
          {debrief === null ? null : (
            <span className="text-legal text-foreground-faint">{debrief}</span>
          )}
        </span>
      </button>
    </li>
  );
}
